import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { useGetTransactionItemDetail } from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import {
  ComponentType,
  TransactionItemsColumn,
} from '@/features/transaction_items/List/type';
import { TransactionItemBasicForm } from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemBasicForm';
import {
  TransactionItemGeneralForm,
  Props as TransactionItemGeneralFormProps,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import { Loading } from '@moneyforward/ap-frontend-components';
import { ApInvoiceTransactionGeneralInput } from 'ap-openapi';
import classNames from 'classnames';
import { FC, Suspense, memo, useEffect, useMemo } from 'react';
import styles from './DetailDrawer.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  selectedDetailId: string;
  setIsDetailLoading: (isLoading: boolean) => void;
  type: TransactionItemsColumn['type'];
} & TransactionItemGeneralFormProps;

type InnerProps = Omit<Props, 'type'>;

const castNumericValue = (value: string | number | undefined | null) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }

  // Value will come from the API as a decimal string.
  // Integers will have a trailing ".0" that needs to be removed
  return String(value).split('.')[0];
};

const toNumberOrDefault = (
  value: string | number | undefined,
  defaultValue: number
) => {
  if (value === undefined) return defaultValue;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const InnerGeneralDetailDrawer: FC<InnerProps> = memo(
  ({ selectedDetailId, setIsDetailLoading, ...props }) => {
    const { data } = useGetTransactionItemDetail(selectedDetailId);

    const castedDataToTransactionItemFormType = useMemo(() => {
      return {
        label: data?.label ?? '',
        is_visible: data?.is_visible ?? false,
        is_required: data?.is_required ?? false,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        component_type: data?.component_type as ComponentType,
        max_chars: toNumberOrDefault(data?.max_chars, 0),
        min_chars: toNumberOrDefault(data?.min_chars, 0),
        min_value: castNumericValue(data?.min_value),
        max_value: castNumericValue(data?.max_value),
        allow_decimal: !!data?.decimal_places,
        placeholder: data?.placeholder ?? '',
        tooltip_message: data?.tooltip_message ?? '',
        date_format_type: data?.date_format_type,
      };
    }, [data]);

    useEffect(() => {
      setIsDetailLoading(false);
    }, [setIsDetailLoading]);

    return (
      <TransactionItemGeneralForm
        {...props}
        itemType={data?.type}
        selectedDetailId={selectedDetailId}
        defaultValues={castedDataToTransactionItemFormType}
      />
    );
  }
);
InnerGeneralDetailDrawer.displayName = 'InnerGeneralDetailDrawer';

const InnerBasicDetailDrawer: FC<InnerProps> = memo(
  ({ setIsDetailLoading, ...props }) => {
    // TODO: Call New API when it's ready
    const data: ApInvoiceTransactionGeneralInput = useMemo(() => {
      return {
        id: 'N5yLO_aNpvuekQdvLI_ptA',
        column_width: 224,
        is_required: true,
        is_visible: true,
        priority: 2,
        component_type: 'text',
        label: '品目',
        tooltip_message: '件名を入力してください',
        system_tooltip: '',
        ref_link: '',
        ref_label: '',
        customizable: false,
        allows_update: true,
        type: 'basic',
        min_chars: undefined,
        max_chars: undefined,
        min_value: undefined,
        max_value: undefined,
        placeholder: undefined,
        date_format_type: undefined,
        decimal_places: undefined,
        can_delete: false,
        delete_tooltip: undefined,
        has_input_values: undefined,
      };
    }, []);

    useEffect(() => {
      setIsDetailLoading(false);
    }, [setIsDetailLoading]);

    return <TransactionItemBasicForm {...props} itemDetails={data} />;
  }
);
InnerBasicDetailDrawer.displayName = 'InnerBasicDetailDrawer';

const LoadingComponent: FC<InnerProps> = memo(({ setIsDetailLoading }) => {
  useEffect(() => {
    setIsDetailLoading(true);
  }, [setIsDetailLoading]);
  return (
    <div className={cx(styles['loading-container'])}>
      <Loading size='large' />
    </div>
  );
});
LoadingComponent.displayName = 'LoadingComponent';

export const DetailDrawer: FC<Props> = memo(({ type, ...props }) => {
  const isBasicItem = type === 'basic';

  return (
    <SuspenseErrorBoundary>
      <Suspense fallback={<LoadingComponent {...props} />}>
        {isBasicItem ? (
          <InnerBasicDetailDrawer {...props} />
        ) : (
          <InnerGeneralDetailDrawer {...props} />
        )}
      </Suspense>
    </SuspenseErrorBoundary>
  );
});
DetailDrawer.displayName = 'DetailDrawer';
